import type { LocalDocument } from './documentModel';

/** Strip 11-digit CUIT prefix from stored filename. */
export function stripCuitPrefix(name: string): string {
  const base = name.split('/').pop() || name;
  return base.match(/^\d{11}-.+/) ? base.replace(/^\d{11}-/, '') : base;
}

/** Strip bulk-upload temp segment: temp-NNN-YYYY-MM-DDTHH-mm-ss-mmmZ- */
export function stripTempUploadSegment(name: string): string {
  return name.replace(
    /^temp-\d{3}-\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}-\d{3}Z-/i,
    ''
  );
}

/** Strip final upload timestamp: -dd-mm-yyyyThh-mm-ss[.ext] */
export function stripFinalTimestamp(name: string): string {
  return name.replace(
    /(.*)-\d{2}-\d{2}-\d{4}T\d{2}-\d{2}-\d{2}(\.[^.]+)?$/,
    '$1$2'
  );
}

export function stripExtension(name: string): string {
  const lastDot = name.lastIndexOf('.');
  return lastDot > 0 ? name.substring(0, lastDot) : name;
}

/** Normalize logical filename for dedup: spaces → underscores, lowercase. */
export function normalizeLogicalFilename(name: string): string {
  return name.replace(/\s+/g, '_').replace(/_+/g, '_').toLowerCase();
}

/** Dedup key: type + normalized logical filename. */
export function buildDocumentDedupKey(
  type?: string | null,
  name?: string | null
): string {
  let normalized = stripCuitPrefix(name || '');
  normalized = stripTempUploadSegment(normalized);
  normalized = stripFinalTimestamp(normalized);
  normalized = stripExtension(normalized);
  normalized = normalizeLogicalFilename(normalized);
  return `${type || ''}::${normalized}`;
}

/** Detect temp segment after CUIT strip. */
export function isTempDocumentPath(name: string): boolean {
  const withoutCuit = stripCuitPrefix(name);
  return /^temp-\d{3}-/i.test(withoutCuit);
}

export type DocumentDedupEntry = {
  file_name: string;
  type: string;
  is_basic_data?: boolean;
  file?: string;
};

/**
 * Extra invalid for save/display when missing type or persisted name.
 * Pending uploads with an attached File remain valid even if temp-only.
 */
export function isInvalidExtraFile(f: Partial<LocalDocument>): boolean {
  if (!f.type) return true;
  const name = (f.asset_file_path || f.file_name || '').toString();
  const hasExt = name.includes('.');
  const isTemp = name.startsWith('temp-');
  const hasFile = Boolean(f.file);
  if (hasFile) return false;
  if (isTemp) return true;
  if (!hasExt) return true;
  return false;
}

export function hasUnpersistedExtraDocuments(
  extras: Array<Partial<LocalDocument>>
): boolean {
  return extras.some((f) => !f.isDeleted && Boolean(f.isPending));
}

/** Semantic + exact dedup for save payload (used by Origination Documents getFilesToSave). */
export function deduplicateDocumentsForSave<T extends DocumentDedupEntry>(
  candidates: T[]
): T[] {
  const dedup = new Map<string, T>();

  candidates.forEach((doc) => {
    const key = buildDocumentDedupKey(doc.type, doc.file_name);
    const existing = dedup.get(key);
    if (!existing) {
      dedup.set(key, doc);
      return;
    }

    if (existing.is_basic_data && !doc.is_basic_data) {
      return;
    }
    if (!existing.is_basic_data && doc.is_basic_data) {
      dedup.set(key, doc);
      return;
    }

    const existingTemp = isTempDocumentPath(existing.file_name || '');
    const currentTemp = isTempDocumentPath(doc.file_name || '');
    if (existingTemp && !currentTemp) {
      dedup.set(key, doc);
    }
  });

  const exactDedup = new Map<string, T>();
  for (const doc of Array.from(dedup.values())) {
    const exactKey = `${doc.type}::${(doc.file_name || '').toLowerCase()}`;
    if (!exactDedup.has(exactKey)) {
      exactDedup.set(exactKey, doc);
    }
  }
  return Array.from(exactDedup.values());
}

/** Display dedup — same semantic rules as save. */
export function deduplicateDocumentsForDisplay<T extends DocumentDedupEntry>(
  candidates: T[]
): T[] {
  return deduplicateDocumentsForSave(candidates);
}

